import React from 'react';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  /** Full-cell colored block style (monday.com status column) */
  fullCell?: boolean;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'neutral',
  size = 'md',
  dot = false,
  fullCell = false,
  className = '',
}) => {
  // Premium winery full-cell status colors (white text on solid bg)
  const fullCellClasses: Record<string, string> = {
    success: 'bg-success-500 text-white',
    warning: 'bg-warning-500 text-white',
    error:   'bg-error-500 text-white',
    info:    'bg-info-500 text-white',
    neutral: 'bg-monday-status-neutral text-white',
  };

  // Standard pill badge (soft premium tint)
  const pillClasses: Record<string, string> = {
    success: 'text-success-700 bg-success-50 border border-success-200',
    warning: 'text-warning-700 bg-warning-50 border border-warning-200',
    error:   'text-error-700 bg-error-50 border border-error-200',
    info:    'text-info-700 bg-info-50 border border-info-200',
    neutral: 'text-monday-text-muted bg-monday-header border border-monday-border-light',
  };

  const dotColors: Record<string, string> = {
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error:   'bg-error-500',
    info:    'bg-info-500',
    neutral: 'bg-monday-status-neutral',
  };

  const sizeClasses: Record<string, string> = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-xs',
    lg: 'px-4 py-1.5 text-sm',
  };

  const dotSizes: Record<string, string> = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2',
  };

  if (fullCell) {
    return (
      <span
        className={`
          flex items-center justify-center
          w-full min-h-[32px] px-2 py-1
          text-xs font-bold text-center
          rounded-full shadow-xs
          ${fullCellClasses[variant]}
          ${className}
        `}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        ${sizeClasses[size]}
        font-semibold
        ${pillClasses[variant]}
        rounded-full
        ${className}
      `}
    >
      {dot && (
        <span className={`${dotSizes[size]} ${dotColors[variant]} rounded-full flex-shrink-0`} />
      )}
      {children}
    </span>
  );
};

export default Badge;
